import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import {
  mergeCitations,
  normalizeCitationMarkers,
} from 'app/pages/loanApplication/components/GuidelineCitations/citationMarkers.utils';
import { markdownContentStyles } from 'app/pages/loanApplication/components/GuidelineCitations/CitedContent/CitedContent.styles';
import { parseFormattedContent } from 'app/pages/loanApplication/components/GuidelineCitations/CitedContent/CitedContent.utils';
import {
  resolveActionStepTypeFromToolName,
  resolveFriendlyToolLabel,
} from 'app/pages/loanApplication/tabs/aiChat/components/ActionPlan/ActionPlan.constants';
import ActionStep from 'app/pages/loanApplication/tabs/aiChat/components/ActionStep/ActionStep';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ActionStepStatus } from 'constants/aiChat';
import { AiProcessStepBlobKind, LoadStatus } from 'constants/aiProcessRuns';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadStepBlobAction,
  loadStepCitationsAction,
  selectCitationsByStep,
  selectMessagesByStep,
  selectStepBlob,
  selectStepById,
  selectStepCitationsStatus,
  selectStepMessagesStatus,
} from 'store/aiProcessRuns';

import { AiActivityTestIds } from '../../AiActivity.testIds';
import { combineCitationStatuses } from '../../AiActivityCitations/AiActivityCitations.utils';
import CitationChip from '../../AiActivityCitations/CitationChip';
import { useAiActivityCitations } from '../../AiActivityCitations/hooks/useAiActivityCitations';
import { messageMetaStyles, transcriptStyles } from '../ExecutionDetailDrawer.styles';

import ProcessCitationCard from './components/ProcessCitationCard';
import StepMessagesPlaceholder from './StepMessagesPlaceholder';
import { emptyToolActivityStyles, finalOutputSectionStyles } from './StepToolCalls.styles';
import {
  buildToolCallContent,
  hasRawCitationMarkers,
  parseStepToolActivity,
  resolveFinalOutputNarrative,
  shouldBadgeToolDisposition,
} from './StepToolCalls.utils';

const StepToolCalls = ({ stepExecutionId }) => {
  const dispatch = useDispatch();
  const messages = useSelector(selectMessagesByStep(stepExecutionId));
  const messagesStatus = useSelector(selectStepMessagesStatus(stepExecutionId));
  const step = useSelector(selectStepById(stepExecutionId));
  const citations = useSelector(selectCitationsByStep(stepExecutionId));
  const stepCitationsStatus = useSelector(selectStepCitationsStatus(stepExecutionId));
  const outputBlob = useSelector(selectStepBlob(stepExecutionId, AiProcessStepBlobKind.OUTPUT));
  const { citations: runCitations, status: runCitationsStatus } = useAiActivityCitations();
  const hasCitations = !!step?.hasCitations;
  const { toolCalls, finalOutput } = useMemo(() => parseStepToolActivity(messages), [messages]);

  // The raw transcript narrative keeps the model's [[CITE:...]] markers; the
  // rendered summary must come from the backend-cleaned OutputResult blob
  // instead (DEV-3591 / LEN-39) so its [^cite:<slug>] markers resolve to the
  // loaded citation cards — matching the chat surface.
  const finalOutputNarrative = resolveFinalOutputNarrative(finalOutput, outputBlob);
  const shouldLoadCleanedOutput = hasRawCitationMarkers(finalOutput);

  // LEN-213: the cleaned blob of an older run (or a step whose copied citation keys the
  // extractor could not hydrate) can still carry raw [[CITE:<number|key>]] markers.
  // Normalise every marker shape so each resolves — against this step's citations
  // and the run-wide list — to the same card / chip AI chat renders.
  const renderedNarrative = useMemo(
    () => normalizeCitationMarkers(finalOutputNarrative),
    [finalOutputNarrative],
  );
  const mergedCitations = useMemo(
    () => mergeCitations(citations, runCitations),
    [citations, runCitations],
  );
  const citationStatus = combineCitationStatuses([
    hasCitations ? stepCitationsStatus : null,
    runCitationsStatus,
  ]);

  // The step model carries hasCitations from the run-steps load, so the fetch
  // is gated on it — a step with no citations never hits the endpoint (avoiding
  // 404 noise) and the markers render away to nothing.
  useEffectSafeStrictMode(() => {
    if (hasCitations) {
      dispatch(loadStepCitationsAction({ stepExecutionId }));
    }
  }, [dispatch, stepExecutionId, hasCitations]);

  // Only synthesis steps whose narrative still carries raw citation markers need
  // the cleaned blob — marker-free steps render the transcript prose directly.
  useEffectSafeStrictMode(() => {
    if (shouldLoadCleanedOutput) {
      dispatch(loadStepBlobAction({ stepExecutionId, kind: AiProcessStepBlobKind.OUTPUT }));
    }
  }, [dispatch, stepExecutionId, shouldLoadCleanedOutput]);

  const toolCallCards = useMemo(
    () =>
      toolCalls.map(({ id, toolName, isError, confirmationDisposition, result }) => {
        const actionStepType = resolveActionStepTypeFromToolName(toolName);

        return {
          id,
          toolName,
          isError,
          actionStepType,
          title: resolveFriendlyToolLabel(toolName) ?? toolName,
          confirmationDisposition: shouldBadgeToolDisposition(toolName, actionStepType)
            ? confirmationDisposition
            : null,
          content: buildToolCallContent({ id, toolName, result }),
        };
      }),
    [toolCalls],
  );

  if (_.isEmpty(messages) && messagesStatus !== LoadStatus.READY) {
    return <StepMessagesPlaceholder status={messagesStatus} stepExecutionId={stepExecutionId} />;
  }

  const hasNoToolActivity = _.isEmpty(messages) || (toolCalls.length === 0 && _.isNil(finalOutput));

  if (hasNoToolActivity) {
    return (
      <Box data-testid={AiActivityTestIds.STEP_TOOL_CALLS_CONTENT} sx={emptyToolActivityStyles}>
        <Typography variant="regular12">No tool activity for this step.</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid={AiActivityTestIds.STEP_TOOL_CALLS_CONTENT} sx={transcriptStyles}>
      {toolCallCards.map(
        ({ id, toolName, isError, title, actionStepType, confirmationDisposition, content }) => (
          <ActionStep
            actionStepType={actionStepType}
            confirmationDisposition={confirmationDisposition}
            content={content}
            key={id}
            status={isError ? ActionStepStatus.ERROR : ActionStepStatus.SUCCESS}
            title={title}
            toolName={toolName}
          />
        ),
      )}
      <Box sx={finalOutputSectionStyles}>
        <Typography sx={messageMetaStyles} variant="bold12">
          Final output
        </Typography>
        {_.isNil(renderedNarrative) ? (
          <Typography sx={messageMetaStyles} variant="regular14">
            No final output recorded.
          </Typography>
        ) : (
          <Box
            data-testid={AiActivityTestIds.STEP_TOOL_CALLS_FINAL_OUTPUT}
            sx={markdownContentStyles}
          >
            {parseFormattedContent(renderedNarrative, {
              citations: mergedCitations,
              citationStatus,
              CitationCard: ProcessCitationCard,
              InlineCitation: CitationChip,
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

StepToolCalls.propTypes = {
  stepExecutionId: PropTypes.string.isRequired,
};

export default memo(StepToolCalls);
